import { LoadingOutlined } from '@ant-design/icons';
import { Row } from 'antd';
import { forwardRef, useEffect } from 'react';
import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts';
import {
    NameType,
    ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

const axisColor = '#A3A3A3';
const gridColor = '#F0F0F0';
const TICK_DEFAULT = 20;

type Props = {
    data: EverydayNote.ResultTotal.TimeSeries;
    loading: boolean;
    updater: () => void;
    isShowAll: boolean;
};

const BubbleGraph = forwardRef<HTMLDivElement, Props>(({ 
    data,
    loading,
    updater,
    isShowAll,
}) => {
    useEffect(() => {
        // data が更新された直後だと、まだレンダリングされていないことがあるので、
        // 少し待ってから updater を呼ぶ。
        setTimeout(() => updater(), 500);
    }, [data]);// eslint-disable-line

    const dataSource: any[] = [];
    data.forEach((item) => {
        const { name, ...candidates } = item;
        const values = Object.values(candidates);
        values.forEach((item, index) => {
            dataSource.push({
                date: name,
                range: item.name,
                count: item.count,
            });
        });
    });

    const sliderRangeMax = Math.max.apply(
        null,
        dataSource.map((entry) => entry.range)
    );

    const sliderRangeMin = Math.min.apply(
        null,
        dataSource.map((entry) => entry.range)
    );

    const ranges = sliderRangeMax - sliderRangeMin;

    const tickCount = isShowAll
        ? sliderRangeMax + 1
        : ranges > TICK_DEFAULT
            ? TICK_DEFAULT + 1
            : ranges + 1;

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 'calc(300px - 20px - 16px)',
                width: '100%',
            }}
            >
                <LoadingOutlined style={{ fontSize: '35px', color: '#004C97' }} />
            </div>
        );
    }

    return (
        <Row
            style={{
                height: `${isShowAll ? ranges < 10 ? 300 : ranges * 30 : 500}px`,
                width: '100%',
            }}
        >
            <ResponsiveContainer width='100%' height='100%'>
                <ScatterChart
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                    }}
                    data={dataSource}
                >
                    <CartesianGrid stroke={gridColor} />
                    <XAxis
                        axisLine={false}
                        allowDuplicatedCategory={false}
                        dataKey='date'
                        stroke={axisColor}
                        tick={CustomizedAxisTick}
                        tickLine={false}
                        type='category'
                        interval={0}
                        name='日付'
                    />

                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        dataKey='range'
                        type='number'
                        domain={[sliderRangeMin, sliderRangeMax]}
                        stroke={axisColor}
                        allowDecimals={false}
                        tickCount={tickCount}
                        interval={0}
                        name='レンジ'
                    />
                    <ZAxis
                        dataKey='count'
                        range={[0, 400]}
                        name='カウント'
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ strokeDasharray: '3 3' }}
                    />
                    <Scatter
                        dataKey='date'
                        fill="#004C97"
                        shape='circle'
                        name='slider'
                    />
                </ScatterChart>
            </ResponsiveContainer>
        </Row>
    );
});

export default BubbleGraph;

const CustomizedAxisTick = (props: {
    x: number;
    y: number;
    payload: { value: string };
}) => {
    const { x, y, payload } = props;
    const dayOfWeekTextHeadIndex = payload.value.indexOf('(');

    const dayText = payload.value.split('(')[0].trim();
    const dayOfWeekText = payload.value
        .substring(dayOfWeekTextHeadIndex)
        .trim();

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} fill='#888'>
                <tspan textAnchor='middle' x='0'>
                    {dayText}
                </tspan>
                <tspan textAnchor='middle' x='0' dy='20'>
                    {dayOfWeekText}
                </tspan>
            </text>
        </g>
    );
};

const CustomTooltip = ({
    active,
    payload,
}: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {

        return (
            <div className='custom-tooltip border border-gray-300 p-2 bg-white'>
                {payload.map((pld, index) => (
                    <div key={index} className='flex gap-2'>
                        <div>{pld.name}:</div>
                        <div>{pld.value}</div>
                    </div>
                ))}
            </div>
        );
    }

    return null;
};